// src/components/leads/LeadTable.tsx
import { useState, useEffect, type ReactNode } from "react";
import {
  FiEdit2,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiFilter,
  FiX,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

import type {
  LeadStatus,
  LeadSource,
  Priority,
  LeadRow,
} from "@/pages/leads/useLeadsPage";

/* ------------------------------ Local types ------------------------------ */

export type LeadsTableFilters = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  status?: LeadStatus | "";
  employeeId?: string | "";
  product?: string;
  dateFrom?: string;
  dateTo?: string;
  source?: LeadSource | "";
  priority?: Priority | "";
};

type LeadTableProps = {
  // data
  pageRows: LeadRow[];
  serialStart: number;
  page: number;
  pageSize: number;
  start: number;
  totalFiltered: number;
  totalPages: number;

  // loading
  isLoading: boolean;
  isError: boolean;
  deleteLoading: boolean;

  // filters / options
  filters: LeadsTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<LeadsTableFilters>>;
  employeeOptions: Array<{ value: string; label: string }>;
  sourceOptions: LeadSource[];
  priorityOptions: Priority[];

  // pagination
  setPage: (page: number) => void;

  // actions
  onEdit: (id: number | string) => void;
  onDelete: (row: LeadRow) => void;
};

/* --------------------------------- Utils --------------------------------- */
const initials = (a: string, b: string) =>
  `${(a?.[0] || "").toUpperCase()}${(b?.[0] || "").toUpperCase()}`;

/* ------------------------------ Reusable UI ------------------------------- */
function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  disabled,
  min,
  step,
}: {
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  step?: number;
}) {
  return (
    <input
      value={value as any}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      required={required}
      disabled={disabled}
      min={min}
      step={step}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
    />
  );
}

function UiSelect<T extends string>({
  value,
  onChange,
  options,
  placeholder = "— Select —",
  required,
}: {
  value?: T | "";
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: T[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={onChange}
      required={required}
      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none
                 text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
    >
      <option value="">
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function ColumnFilter({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest?.("[data-popover]")) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  return (
    <div className="relative" data-popover>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:text-gray-700"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        <FiFilter />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-lg max-h-72 overflow-y-auto">
          <div className="mb-2 text-xs font-semibold text-gray-700">
            {label} Filter
          </div>
          <div className="space-y-2">{children}</div>
          <div className="mt-3 text-right">
            <button
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmDialog({
  open,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              onClick={onCancel}
            >
              <FiX />
            </button>
          </div>
          <div className="p-5 text-sm text-gray-700">{message}</div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              onClick={onCancel}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <th
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}

function StatusChip({ status }: { status: LeadStatus }) {
  const cfg: Record<
    LeadStatus,
    { wrap: string; text: string; icon: ReactNode }
  > = {
    Pending: {
      wrap: "bg-amber-50 text-amber-700",
      text: "Pending",
      icon: <span>●</span>,
    },
    Completed: {
      wrap: "bg-green-50 text-green-700",
      text: "Completed",
      icon: <FiCheckCircle />,
    },
    Cancelled: {
      wrap: "bg-red-50 text-red-700",
      text: "Cancelled",
      icon: <FiXCircle />,
    },
  };
  const c = cfg[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${c.wrap}`}
    >
      {c.icon} {c.text}
    </span>
  );
}

function ProductsPills({ products }: { products: string[] }) {
  if (!products || products.length === 0) {
    return <span className="text-gray-400">—</span>;
  }

  const visible = products.slice(0, 2);
  const extra = products.length - visible.length;

  return (
    <div className="flex items-center gap-1 max-w-[220px] overflow-hidden">
      {visible.map((name, idx) => (
        <span
          key={idx}
          className="inline-flex max-w-[170px] items-center truncate whitespace-nowrap rounded-full border border-sky-100 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700"
          title={name}
        >
          {name}
        </span>
      ))}

      {extra > 0 && (
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
          +{extra} more
        </span>
      )}
    </div>
  );
}


/* ------------------------------ Component -------------------------------- */

export default function LeadTable({
  pageRows,
  serialStart,
  page,
  pageSize,
  start,
  totalFiltered,
  totalPages,
  isLoading,
  isError,
  deleteLoading,
  filters,
  setFilters,
  employeeOptions,
  sourceOptions,
  priorityOptions,
  setPage,
  onEdit,
  onDelete,
}: LeadTableProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  const askDelete = (row: LeadRow) => {
    setConfirmMsg(
      `Delete lead "${row.firstName} ${row.lastName}"? This action cannot be undone.`
    );
    setConfirmAction(() => () => {
      onDelete(row);
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {/* Desktop table */}
        <div className="hidden md:block relative max-h-[70vh] overflow-x-auto overflow-y-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="border-b border-gray-200">
                <Th>S.No</Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>First name</span>
                    <ColumnFilter label="First name">
                      <Input
                        placeholder="Contains…"
                        value={filters.firstName || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            firstName: e.target.value,
                          }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>Last name</span>
                    <ColumnFilter label="Last name">
                      <Input
                        placeholder="Contains…"
                        value={filters.lastName || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            lastName: e.target.value,
                          }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>Phone</span>
                    <ColumnFilter label="Phone">
                      <Input
                        placeholder="Contains…"
                        value={filters.phone || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            phone: e.target.value,
                          }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th className="w-[240px]">
                  <div className="flex items-center gap-1.5">
                    <span>Email</span>
                    <ColumnFilter label="Email">
                      <Input
                        placeholder="Contains…"
                        value={filters.email || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            email: e.target.value,
                          }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th className="w-[260px]">
                  <div className="flex items-center gap-1.5">
                    <span>Date</span>
                    <ColumnFilter label="Date range">
                      <Input
                        type="date"
                        value={filters.dateFrom || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            dateFrom: e.target.value,
                          }))
                        }
                      />
                      <Input
                        type="date"
                        value={filters.dateTo || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            dateTo: e.target.value,
                          }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th className="w-[260px]">
                  <div className="flex items-center gap-1.5">
                    <span>Products</span>
                    <ColumnFilter label="Products">
                      <Input
                        placeholder="Contains…"
                        value={filters.product || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            product: e.target.value,
                          }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>Status</span>
                    <ColumnFilter label="Status">
                      <UiSelect
                        value={filters.status || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            status: e.target.value as LeadStatus | "",
                          }))
                        }
                        options={["Pending", "Completed", "Cancelled"]}
                        placeholder="— Any —"
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>Lead Owner</span>
                    <ColumnFilter label="Lead Owner">
                      <select
                        value={filters.employeeId || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            employeeId: e.target.value,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none text-gray-900 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                      >
                        <option value="">— Any —</option>
                        {employeeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </ColumnFilter>
                  </div>
                </Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>Source</span>
                    <ColumnFilter label="Source">
                      <UiSelect
                        value={filters.source || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            source: e.target.value as LeadSource | "",
                          }))
                        }
                        options={sourceOptions}
                        placeholder="— Any —"
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th>
                  <div className="flex items-center gap-1.5">
                    <span>Priority</span>
                    <ColumnFilter label="Priority">
                      <UiSelect
                        value={filters.priority || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            priority: e.target.value as Priority | "",
                          }))
                        }
                        options={priorityOptions}
                        placeholder="— Any —"
                      />
                    </ColumnFilter>
                  </div>
                </Th>

                <Th className="text-right">Actions</Th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={12}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Loading leads…
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={12}
                    className="px-5 py-10 text-center text-sm text-red-600"
                  >
                    Failed to load leads. Please try again.
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={12}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    No leads found.
                  </td>
                </tr>
              ) : (
                pageRows.map((l, i) => (
                  <tr
                    key={l.id}
                    className="cursor-pointer hover:bg-gray-50/60"
                    onClick={() => onEdit(l.id)}
                  >
                    <Td className="text-gray-700">{serialStart + i + 1}</Td>

                    <Td>
                      <div className="flex items-center gap-2.5">
                        <div className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                          {initials(l.firstName, l.lastName)}
                        </div>
                        <span className="text-gray-900">{l.firstName}</span>
                      </div>
                    </Td>

                    <Td className="text-gray-900">{l.lastName}</Td>
                    <Td className="text-gray-700">{l.phone}</Td>
                    <Td className="text-gray-700 truncate">{l.email}</Td>
                    <Td className="whitespace-nowrap text-gray-700">{String(l.date ?? "").slice(0, 10)}</Td>

                    {/* ✅ Product column: "Name" or "Name +" (string already prepared) */}
                    <Td className="text-gray-700">
                      <ProductsPills products={l.products} />
                    </Td>


                    <Td>
                      <StatusChip status={l.status} />
                    </Td>
                    <Td className="text-gray-700">{l.employee || "—"}</Td>
                    <Td className="text-gray-700">{l.source || "—"}</Td>
                    <Td className="text-gray-700">{l.priority || "—"}</Td>

                    <Td>
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(l.id);
                          }}
                          aria-label="Edit"
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            askDelete(l);
                          }}
                          aria-label="Delete"
                          title="Delete"
                          disabled={deleteLoading}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile list */}
        <div className="md:hidden divide-y divide-gray-200">
          {isLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Loading leads…
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-sm text-red-600">
              Failed to load leads. Please try again.
            </div>
          ) : pageRows.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              No leads found.
            </div>
          ) : (
            pageRows.map((l, i) => (
              <div
                key={l.id}
                className="cursor-pointer p-4"
                onClick={() => onEdit(l.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                      {initials(l.firstName, l.lastName)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {serialStart + i + 1}. {l.firstName} {l.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{l.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(l.id);
                      }}
                      aria-label="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        askDelete(l);
                      }}
                      aria-label="Delete"
                      disabled={deleteLoading}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                  <div>
                    <span className="text-gray-500">Phone:</span> {l.phone}
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span> {String(l.date ?? "").slice(0, 10)}
                  </div>
                  {/* Lead owner on mobile */}
                  <div>
                    <span className="text-gray-500">Lead Owner:</span>{" "}
                    {l.employee || "—"}
                  </div>
                  <div className="col-span-2 flex items-start gap-1">
                    <span className="mt-0.5 text-gray-500">Products:</span>
                    <ProductsPills products={l.products} />
                  </div>

                  <div className="col-span-2">
                    <StatusChip status={l.status} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
          <div className="text-xs text-gray-500">
            Showing{" "}
            <span className="font-medium text-gray-700">
              {totalFiltered === 0 ? 0 : start + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-700">
              {Math.min(start + pageSize, totalFiltered)}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700">
              {totalFiltered}
            </span>{" "}
            results
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <FiChevronLeft /> Prev
            </button>
            <div className="hidden sm:flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
                const n = idx + 1;
                const active = n === page;
                return (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`rounded-md px-2.5 py-1.5 text-sm ${
                      active
                        ? "bg-sky-600 text-white"
                        : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
              {totalPages > 6 && (
                <span className="px-1 text-sm text-gray-500">…</span>
              )}
            </div>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete lead"
        message={confirmMsg}
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </>
  );
}
